import { AfterViewInit, Component, HostListener, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subject, combineLatest } from 'rxjs';
import { debounceTime, delay, distinctUntilChanged, filter, map, startWith, tap } from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import { ScrollbarComponent } from 'src/app/shared/components/scrollbar/scrollbar.component';
import { LayoutService } from 'src/app/shared/service/layout.service';
import { LocalStorageService } from 'src/app/shared/service/localstorage.service';
import { HeaderLevel2MenuComponent } from '../header-level2-menu/header-level2-menu.component';
import { LeftMenuService } from '../left-menu/left-menu.service';

const LEFT_MENU_WIDTH = {
  'web-close': '6.8rem',
  'web-open': '292px',
  'h5-close': '0px',
  'h5-open': '100vw',
  hide: '0px',
};
const RIGHT_MENU_WIDTH = {
  'web-open': '375px',
};

@UntilDestroy()
@Component({
  selector: 'app-basic-layout',
  templateUrl: './basic-layout.component.html',
  styleUrls: ['./basic-layout.component.scss'],
  host: { class: 'flex' },
})
export class BasicLayoutComponent implements OnInit, AfterViewInit {
  leftMenuMode$: Subject<'side' | 'over'> = new Subject<'side' | 'over'>();
  leftMenuState: boolean = false;
  leftMenuBackdrop: boolean = false;
  leftMenuWidth!: string;
  layoutContentMarginLeft!: string;

  fullMode!: boolean;
  conciseMode!: boolean;

  /** 左侧菜单是否打开 */
  isOpen: boolean = false;
  isH5!: boolean;
  logined: boolean = false;

  /** 右侧弹窗 */
  isOpenRightCombinePopup: boolean = false;
  layoutContentMarginRight!: string;

  @ViewChild('scrollbar') scrollbar!: ScrollbarComponent;
  @ViewChild('level2Menu') level2Menu!: HeaderLevel2MenuComponent;

  /**当前时间 */
  now: number = Date.now();

  /**关闭后静默时长 */
  duration: number = 7 * 24 * 60 * 60 * 1000;

  /** 头部组件向上移动多少个百分比 */
  headTranlateY: number = 0;

  /** 上次滑动时间 */
  prevTime: number = Date.now();

  /** 上一次滑动位置 */
  prevPos: number = 0;

  /** 纪录上一次页面 */
  lastPage: string = '';

  get noShadow(): boolean {
    return this.leftMenuWidth === LEFT_MENU_WIDTH['hide'];
  }

  get active(): boolean {
    return false;
    const local = this.localStorageService.appDownloadTips;
    if (!local || isNaN(Number(local))) return true;
    return this.now - Number(local) >= this.duration;
  }

  isDesktop: boolean = this.deviceService.isDesktop();
  /** 是否有二级菜单 */
  hideLevel2Head: boolean = false;
  constructor(
    public layout: LayoutService,
    private localStorageService: LocalStorageService,
    private appService: AppService,
    public leftMenuService: LeftMenuService,
    private deviceService: DeviceDetectorService,
  ) {
    //左侧菜单状态判断
    combineLatest([
      this.layout.isH5$,
      this.layout.leftMenuState$,
      this.leftMenuMode$.pipe(startWith(this.getLeftMenuMode(document.body.offsetWidth))),
      this.layout.leftMenuInvisible$.pipe(startWith(undefined)),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([isH5, state, mode, invisible]) => {
        this.isH5 = isH5;
        if (!isH5 && this.isDesktop) this.headTranlateY = 0;
        this.leftMenuState = state;
        if (isH5) this.layout.lockLayoutScroll$.next(state);
        if (invisible) {
          this.leftMenuWidth = this.layoutContentMarginLeft = LEFT_MENU_WIDTH['hide'];
          this.leftMenuBackdrop = false;
        } else {
          this.leftMenuWidth = state
            ? isH5
              ? LEFT_MENU_WIDTH['h5-open']
              : LEFT_MENU_WIDTH['web-open']
            : isH5
              ? LEFT_MENU_WIDTH['h5-close']
              : LEFT_MENU_WIDTH['web-close'];
          this.layoutContentMarginLeft = isH5
            ? LEFT_MENU_WIDTH['h5-close']
            : mode === 'side'
              ? this.leftMenuWidth
              : LEFT_MENU_WIDTH['web-close'];
          this.leftMenuBackdrop = state && mode === 'over';
          this.localStorageService.setValueByJsonStringify('platform-left-menu-state', state);
        }
        this.layout.leftMenuBackdrop$.next(this.leftMenuBackdrop);
      });

    //右侧选单状态判断
    //对会触发动画效果的订阅进行监听，模拟动画开始和完成
    [this.layout.leftMenuState$, this.layout.leftMenuInvisible$].forEach(_$ => {
      _$.pipe(
        distinctUntilChanged(),
        tap(() => this.layout.leftMenuTransition$.next('start')),
        delay(400), // 需与动画时间一致
        tap(() => this.layout.leftMenuTransition$.next('end')),
        untilDestroyed(this),
      ).subscribe();
    });

    //页面宽度变动，设置左侧菜单模式
    this.layout.resize$.pipe(debounceTime(100), untilDestroyed(this)).subscribe(v => {
      const mode = this.getLeftMenuMode(v[0]);
      this.leftMenuMode$.next(mode);
    });

    //每次页面改变置顶，并判断当前页面，设置简洁模式等
    this.layout.page$.pipe(untilDestroyed(this)).subscribe(page => {
      if (this.lastPage != page) {
        if (this.isH5 && !this.isDesktop) {
          this.headTranlateY = 0;
          this.layout.scrollToTop();
        } else {
          this.scrollbar?.scrollToTop();
        }
      }
      this.lastPage = page;

      //设置无footer
      this.fullMode = false;
      this.layout.getComponentTree().forEach(x => {
        if (x._displayMode) {
          this.fullMode = {
            FullMode: true,
            normal: false,
          }[x._displayMode as 'FullMode' | 'normal'];
        }
      });

      if (['register', 'login', 'password'].includes(page)) {
        this.conciseMode = true;
        this.layout.leftMenuInvisible$.next(true);
      } else if (this.conciseMode) {
        this.conciseMode = false;
        this.layout.leftMenuInvisible$.next(false);
      }
    });
    combineLatest([
      this.leftMenuMode$.pipe(startWith(this.getLeftMenuMode(document.body.offsetWidth))),
      this.layout.rightCombinePupup$,
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([mode, state]) => {
        this.isOpenRightCombinePopup = state;
        if (this.isH5) this.layout.lockLayoutScroll$.next(state);
        if (this.isOpenRightCombinePopup) {
          this.layoutContentMarginRight = this.isH5 ? '0px' : mode == 'side' ? RIGHT_MENU_WIDTH['web-open'] : '0px';
        } else {
          this.layoutContentMarginRight = '0px';
        }
      });

    this.appService.userInfo$.pipe(untilDestroyed(this)).subscribe(v => {
      this.logined = !!v;
    });

    this.layout.scrollTop$.pipe(untilDestroyed(this)).subscribe(v => {
      if (v) {
        this.onScroll(v);
      }
    });

    //

    this.leftMenuService
      .onGetTags()
      .pipe(
        untilDestroyed(this),
        map(v => {
          const headerMenu = v?.navigationMenus || [];
          return headerMenu;
        }),
      )
      .subscribe(data => {
        this.hideLevel2Head = data.length > 0 ? true : false;
      });
  }

  ngOnInit() {
    // const localLeftMenuStateState = this.localStorageService.getValueByJsonParse('platform-left-menu-state', false);
    // this.layout.leftMenuState$.next(localLeftMenuStateState);
    const localLeftMenuStateState = this.localStorageService.getValueByJsonParse('platform-left-menu-state', true);
    this.layout.leftMenuState$.next(
      this.layout.isH5$.value ? false : document.body.offsetWidth > 1200 ? localLeftMenuStateState : false,
    );
  }
  ngAfterViewInit(): void {
    this.layout.scrollToTopAnimation$
      .pipe(
        untilDestroyed(this),
        filter(x => x),
      )
      .subscribe(() => {
        if (this.isH5 && !this.isDesktop) {
          this.headTranlateY = 0;
          this.layout.scrollToTopAnimation();
        } else {
          this.scrollbar?.scrollToTopAnimation();
        }
      });
  }

  getLeftMenuMode(documentWidth: number) {
    return documentWidth >= 1280 ? 'side' : 'over';
  }

  /**
   * for scrollbar component
   *
   * @param event
   */
  onScrollDistance(event: number) {
    this.layout.scrollTop$.next(event);
  }

  @HostListener('window:scroll', ['event'])
  initBasicScorll() {
    if (this.isH5) {
      const currentPos = (document.querySelector('html') as HTMLHtmlElement).scrollTop;
      this.layout.scrollTop$.next(currentPos);
    }
  }
  onScroll(currentPos: number) {
    if (this.isH5 && !this.isDesktop) {
      const currentTime = Date.now();
      const timeDiff = currentTime - this.prevTime;
      const scrollDiff = currentPos - this.prevPos;
      const speed = Math.ceil(Math.abs(scrollDiff / timeDiff));
      this.prevTime = currentTime;
      this.prevPos = currentPos;
      const fixedSpeed = 1;
      const offsetTop = 100;

      if (scrollDiff < 0) {
        // 向上滑动
        if (currentPos < offsetTop || speed > fixedSpeed) {
          this.headTranlateY = 0;
        }
      } else if (scrollDiff > 0) {
        //向下滑动
        if (speed > fixedSpeed) {
          this.headTranlateY = -offsetTop;
          if (!this.appService.isNativeApp) {
            this.level2Menu?.closePop();
          }
        }
      }
    }
  }

  onScrollOldIos($event: any) {
    this.onScroll($event.target.scrollTop);
  }
}
